// Components/ModalVincularCategoria/index.tsx
import React, { useState, useEffect, useCallback } from 'react';
import { Modal, Button, Form, Row, Col } from 'react-bootstrap';
import { useAppSelector } from '../../redux/store';
import api from '../../Services/api';
import Globals from '../../Globals.json';
import { toast } from 'react-toastify';
import { notifyError, notify } from '../notify/notify-component';
import TreeCategorySelect from '../TreeCategorySelect';
import * as S from './styled';

interface Props {
  show: boolean;
  onHide: () => void;
}

interface PerfilFiscal {
  id: number;
  tax_profile_name: string;
  uuid: string;
}

interface BulkAttributes {
  product_purpose_id?: number;
  self_service?: boolean;
  xd_printers?: string;
  xd_follow_item?: boolean;
  is_active?: boolean;
  xd_request_qty?: boolean;
  xd_request_price_when_zero?: boolean;
  xd_balance_qty?: boolean;
  xd_order_grouped?: boolean;
  xd_bill_group?: number;
  xd_add_tips?: boolean;
  xd_exclude_minimum_consumption?: boolean;
  xd_book?: boolean;
  visible_falimix?: boolean;
  visible_autoplu?: boolean;
  visible_delivery?: boolean;
  visible_digital_menu?: boolean;
  visible_online?: boolean;
  visible_xd_orders?: boolean;
}

interface BulkUpdatePayload {
  store_id: number;
  confirm: boolean;
  apply_to_categories: boolean;
  apply_to_products: boolean;
  attributes: BulkAttributes;
}

export function ModalVincularCategoria({ show, onHide }: Props) {
  const storeMe = useAppSelector(({ storeMeReducer }) => storeMeReducer.value);
  
  const [perfisFiscais, setPerfisFiscais] = useState<PerfilFiscal[]>([]);
  const [treeData, setTreeData] = useState<any[]>([]);
  const [perfilSelecionado, setPerfilSelecionado] = useState<number>(0);
  const [categoriasSelecionadas, setCategoriasSelecionadas] = useState<number[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [loadingData, setLoadingData] = useState<boolean>(false);
  
  // Estados para atributos em massa
  const [showBulkSection, setShowBulkSection] = useState<boolean>(false);
  const [applyToCategories, setApplyToCategories] = useState<boolean>(true);
  const [applyToProducts, setApplyToProducts] = useState<boolean>(true);
  const [showConfirmModal, setShowConfirmModal] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [bulkAttributes, setBulkAttributes] = useState<BulkAttributes>({
    product_purpose_id: undefined,
    self_service: true,
    xd_printers: '',
    xd_follow_item: true,
    is_active: true,
    xd_request_qty: true,
    xd_request_price_when_zero: true,
    xd_balance_qty: true,
    xd_order_grouped: true,
    xd_bill_group: 0,
    xd_add_tips: true,
    xd_exclude_minimum_consumption: true,
    xd_book: true,
    visible_falimix: true,
    visible_autoplu: true,
    visible_delivery: true,
    visible_digital_menu: true,
    visible_online: true,
    visible_xd_orders: true
  });

  // Função auxiliar para obter todos os IDs de uma categoria e suas filhas
  const getAllChildrenIds = (category: any): number[] => {
    let ids: number[] = [category.id];
    
    if (category.relationships?.children && category.relationships.children.length > 0) {
      category.relationships.children.forEach((child: any) => {
        ids = [...ids, ...getAllChildrenIds(child)];
      });
    }
    
    return ids;
  };

  const carregarDados = useCallback(async () => {
    setLoadingData(true);
    try {
      // Carregar perfis fiscais
      const perfilResponse = await api.get(`${Globals.api.taxProfile}?store_id=${storeMe?.storeActive?.id}`);
      setPerfisFiscais(perfilResponse.data.data || []);

      // Carregar categorias em árvore
      const categoriaResponse = await api.get(`${Globals.api.treeCategories}`, {
        params: {
          page: 1,
          paginate: false,
          store_id: storeMe?.storeActive?.id,
        }
      });
      
      setTreeData(categoriaResponse.data.data || []);
    } catch (error) {
      notifyError('Erro ao carregar dados');
      console.error('Erro ao carregar dados:', error);
    } finally {
      setLoadingData(false);
    }
  }, [storeMe?.storeActive?.id]);

  useEffect(() => {
    if (show) {
      carregarDados();
    }
  }, [show, carregarDados]);

  const handleToggleCategory = (category: any) => {
    setCategoriasSelecionadas(prev => {
      const isCurrentlySelected = prev.includes(category.id);
      
      // Obter todos os IDs desta categoria e suas filhas
      const allIds = getAllChildrenIds(category);
      
      if (isCurrentlySelected) {
        // Se está selecionada, remover esta categoria e todas as filhas
        return prev.filter(id => !allIds.includes(id));
      } else {
        // Se não está selecionada, adicionar esta categoria e todas as filhas
        const newIds = [...prev];
        
        allIds.forEach(id => {
          if (!newIds.includes(id)) {
            newIds.push(id);
          }
        });
        
        return newIds;
      }
    });
  };

  const isCategorySelected = (categoryId: number) => {
    return categoriasSelecionadas.includes(categoryId);
  };

  const handleSelecionarTodas = () => {
    if (categoriasSelecionadas.length > 0) {
      setCategoriasSelecionadas([]);
    } else {
      // Função auxiliar para coletar todos os IDs de categorias (incluindo filhas)
      const getAllCategoryIds = (categories: any[]): number[] => {
        let ids: number[] = [];
        categories.forEach(cat => {
          ids.push(cat.id);
          if (cat.relationships?.children && cat.relationships.children.length > 0) {
            ids = [...ids, ...getAllCategoryIds(cat.relationships.children)];
          }
        });
        return ids;
      };
      
      setCategoriasSelecionadas(getAllCategoryIds(treeData));
    }
  };

  const handleBulkAttributesUpdate = async () => {
    if (!storeMe?.storeActive?.id) {
      toast.warning('Loja não identificada');
      return;
    }

    setIsLoading(true);
    try {
      const payload: BulkUpdatePayload = {
        store_id: storeMe.storeActive.id,
        confirm: true,
        apply_to_categories: applyToCategories,
        apply_to_products: applyToProducts,
        attributes: bulkAttributes
      };

      await api.post('/api/v1/bulk-attributes/update-store', payload);
      
      notify('Atributos atualizados em massa com sucesso!');
      handleClose();
    } catch (error) {
      notifyError('Erro ao atualizar atributos em massa');
      console.error('Erro ao atualizar atributos:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleConfirmBulkUpdate = () => {
    setShowConfirmModal(false);
    handleBulkAttributesUpdate();
  };

  const handleSalvar = async () => {
    if (showBulkSection) {
      setShowConfirmModal(true);
      return;
    }

    if (!perfilSelecionado) {
      toast.warning('Selecione um perfil fiscal');
      return;
    }

    if (categoriasSelecionadas.length === 0) {
      toast.warning('Selecione pelo menos uma categoria');
      return;
    }

    setLoading(true);
    try {
      const promises = categoriasSelecionadas.map(categoriaId => {
        return api.post('/api/v1/categories/attach-tax-profile', {
          category_id: categoriaId,
          tax_profile_id: perfilSelecionado
        });
      });

      await Promise.all(promises);
      
      notify(`${categoriasSelecionadas.length} categoria(s) vinculada(s) com sucesso!`);
      handleClose();
      carregarDados(); // Recarregar dados após vincular
    } catch (error) {
      notifyError('Erro ao vincular categorias');
      console.error('Erro ao vincular categorias:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setPerfilSelecionado(0);
    setCategoriasSelecionadas([]);
    setShowBulkSection(false);
    setApplyToCategories(true);
    setApplyToProducts(true);
    setShowConfirmModal(false);
    setIsLoading(false);
    setBulkAttributes({
      product_purpose_id: undefined,
      self_service: true,
      xd_printers: '',
      xd_follow_item: true,
      is_active: true,
      xd_request_qty: true,
      xd_request_price_when_zero: true,
      xd_balance_qty: true,
      xd_order_grouped: true,
      xd_bill_group: 0,
      xd_add_tips: true,
      xd_exclude_minimum_consumption: true,
      xd_book: true,
      visible_falimix: true,
      visible_autoplu: true,
      visible_delivery: true,
      visible_digital_menu: true,
      visible_online: true,
      visible_xd_orders: true
    });
    onHide();
  };

  const perfilSelecionadoNome = perfisFiscais.find(p => p.id === perfilSelecionado)?.tax_profile_name || '';

  return (
    <>
      <S.ModalWrapper show={show} onHide={handleClose} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title>
            {showBulkSection ? '⚙️ Configurações de Atributos em Massa' : '🔗 Vincular Categorias ao Perfil Fiscal'}
          </Modal.Title>
        </Modal.Header>
      
        <Modal.Body>
          {loadingData ? (
            <div className="text-center p-4">
              <div className="spinner-border" role="status">
                <span className="visually-hidden">Carregando...</span>
              </div>
            </div>
          ) : (
            <S.Container>
              {/* Botões de navegação */}
              <Row className="mb-4">
                <Col md={12}>
                  <div className="d-flex gap-2">
                    <Button 
                      variant={!showBulkSection ? 'primary' : 'outline-primary'}
                      onClick={() => setShowBulkSection(false)}
                      size="sm"
                    >
                      🔗 Vincular Perfil Fiscal
                    </Button>
                    <Button 
                      variant={showBulkSection ? 'primary' : 'outline-primary'}
                      onClick={() => setShowBulkSection(true)}
                      size="sm"
                    >
                      ⚙️ Atributos em Massa
                    </Button>
                  </div>
                </Col>
              </Row>

              {!showBulkSection && (
                <>
                  {/* Seleção de Perfil Fiscal */}
                  <Row className="mb-4">
                    <Col md={12}>
                      <Form.Group>
                        <Form.Label><strong>Selecione o Perfil Fiscal:</strong></Form.Label>
                        <Form.Select 
                          value={perfilSelecionado} 
                          onChange={(e) => setPerfilSelecionado(Number(e.target.value))}
                          className="mb-3"
                        >
                          <option value={0}>Selecione um perfil fiscal...</option>
                          {perfisFiscais.map(perfil => (
                            <option key={perfil.id} value={perfil.id}>
                              {perfil.tax_profile_name}
                            </option>
                          ))}
                        </Form.Select>
                      </Form.Group>
                    </Col>
                  </Row>

                  {/* Contador e botão de selecionar todas */}
                  {categoriasSelecionadas.length > 0 && (
                    <div style={{ 
                      padding: '0.5rem 1rem', 
                      fontWeight: 'bold', 
                      color: '#009ef7',
                      backgroundColor: '#e7f3ff',
                      borderRadius: '6px',
                      marginBottom: '1rem'
                    }}>
                      {categoriasSelecionadas.length} categoria(s) selecionada(s)
                    </div>
                  )}

                  <Row className="mb-3">
                    <Col md={12}>
                      <div className="d-flex justify-content-between align-items-center">
                        <h6>Selecione as Categorias:</h6>
                        <Button 
                          variant="outline-primary" 
                          size="sm"
                          onClick={handleSelecionarTodas}
                        >
                          {categoriasSelecionadas.length > 0 ? 'Desmarcar Todas' : 'Selecionar Todas'}
                        </Button>
                      </div>
                    </Col>
                  </Row>

                  {/* Árvore de categorias */}
                  <div style={{ 
                    padding: '1rem 0', 
                    overflowY: 'auto', 
                    maxHeight: '400px',
                    minHeight: '300px'
                  }}>
                    {treeData.length > 0 ? (
                      <div className="tree-container">
                        {treeData.map((tree: any) => (
                          <TreeCategorySelect
                            key={tree.id}
                            category={tree}
                            onToggleCategory={handleToggleCategory}
                            isSelected={isCategorySelected(tree.id)}
                            disabled={loading}
                            isCategorySelected={isCategorySelected}
                          />
                        ))}
                      </div>
                    ) : (
                      <div style={{ 
                        textAlign: 'center', 
                        padding: '2rem', 
                        color: '#999' 
                      }}>
                        Nenhuma categoria encontrada.
                      </div>
                    )}
                  </div>

                  {/* Resumo */}
                  {perfilSelecionado > 0 && categoriasSelecionadas.length > 0 && (
                    <div className="mt-3 p-3 bg-light rounded">
                      <strong>Resumo:</strong>
                      <p className="mb-1">
                        Perfil Fiscal: <strong>{perfilSelecionadoNome}</strong>
                      </p>
                      <p className="mb-0">
                        Categorias selecionadas: <strong>{categoriasSelecionadas.length}</strong>
                      </p>
                    </div>
                  )}
                </>
              )}

              {/* Seção de Atributos em Massa */}
              {showBulkSection && (
                <>
                  <Row className="mb-3">
                    <Col md={12}>
                      <h6>🎯 Aplicar configurações para:</h6>
                      <div className="d-flex gap-3 mb-3">
                        <Form.Check
                          type="checkbox"
                          id="apply-categories"
                          label="📁 Categorias"
                          checked={applyToCategories}
                          onChange={(e) => setApplyToCategories(e.target.checked)}
                        />
                        <Form.Check
                          type="checkbox"
                          id="apply-products"
                          label="📦 Produtos"
                          checked={applyToProducts}
                          onChange={(e) => setApplyToProducts(e.target.checked)}
                        />
                      </div>
                    </Col>
                  </Row>

                  {/* Checkboxes para configurações booleanas */}
                  <Row>
                    <Col md={12}>
                      <h6 className="mb-3">Configurações Gerais:</h6>
                      {/* Seção: Configurações Gerais */}
                      <div className="mb-4">
                        <h6 className="text-primary mb-3 border-bottom pb-2">
                          <i className="fas fa-cog me-2"></i>Configurações Gerais
                        </h6>
                        <div className="row">
                          <div className="col-md-6">
                            <Form.Check
                              type="checkbox"
                              id="is-active"
                              label="Ativo"
                              checked={bulkAttributes.is_active || false}
                              onChange={(e) => setBulkAttributes(prev => ({ ...prev, is_active: e.target.checked }))}
                              className="mb-2"
                            />
                            <Form.Check
                              type="checkbox"
                              id="self-service"
                              label="Self Service"
                              checked={bulkAttributes.self_service || false}
                              onChange={(e) => setBulkAttributes(prev => ({ ...prev, self_service: e.target.checked }))}
                              className="mb-2"
                            />
                          </div>
                          <div className="col-md-6">
                            <Form.Check
                              type="checkbox"
                              id="xd-follow-item"
                              label="XD Seguir Item"
                              checked={bulkAttributes.xd_follow_item || false}
                              onChange={(e) => setBulkAttributes(prev => ({ ...prev, xd_follow_item: e.target.checked }))}
                              className="mb-2"
                            />
                            <Form.Check
                              type="checkbox"
                              id="xd-book"
                              label="XD Livro"
                              checked={bulkAttributes.xd_book || false}
                              onChange={(e) => setBulkAttributes(prev => ({ ...prev, xd_book: e.target.checked }))}
                              className="mb-2"
                            />
                          </div>
                        </div>
                      </div>

                      {/* Seção: Configurações de Pedido */}
                      <div className="mb-4">
                        <h6 className="text-primary mb-3 border-bottom pb-2">
                          <i className="fas fa-shopping-cart me-2"></i>Configurações de Pedido
                        </h6>
                        <div className="row">
                          <div className="col-md-6">
                            <Form.Check
                              type="checkbox"
                              id="xd-request-qty"
                              label="XD Solicitar Quantidade"
                              checked={bulkAttributes.xd_request_qty || false}
                              onChange={(e) => setBulkAttributes(prev => ({ ...prev, xd_request_qty: e.target.checked }))}
                              className="mb-2"
                            />
                            <Form.Check
                              type="checkbox"
                              id="xd-request-price-when-zero"
                              label="XD Solicitar Preço Quando Zero"
                              checked={bulkAttributes.xd_request_price_when_zero || false}
                              onChange={(e) => setBulkAttributes(prev => ({ ...prev, xd_request_price_when_zero: e.target.checked }))}
                              className="mb-2"
                            />
                            <Form.Check
                              type="checkbox"
                              id="xd-balance-qty"
                              label="XD Saldo Quantidade"
                              checked={bulkAttributes.xd_balance_qty || false}
                              onChange={(e) => setBulkAttributes(prev => ({ ...prev, xd_balance_qty: e.target.checked }))}
                              className="mb-2"
                            />
                          </div>
                          <div className="col-md-6">
                            <Form.Check
                              type="checkbox"
                              id="xd-order-grouped"
                              label="XD Pedido Agrupado"
                              checked={bulkAttributes.xd_order_grouped || false}
                              onChange={(e) => setBulkAttributes(prev => ({ ...prev, xd_order_grouped: e.target.checked }))}
                              className="mb-2"
                            />
                            <Form.Check
                              type="checkbox"
                              id="xd-add-tips"
                              label="XD Adicionar Gorjetas"
                              checked={bulkAttributes.xd_add_tips || false}
                              onChange={(e) => setBulkAttributes(prev => ({ ...prev, xd_add_tips: e.target.checked }))}
                              className="mb-2"
                            />
                            <Form.Check
                              type="checkbox"
                              id="xd-bill-group"
                              label="Agrupado na conta"
                              checked={bulkAttributes.xd_bill_group === 1}
                              onChange={(e) => setBulkAttributes(prev => ({ ...prev, xd_bill_group: e.target.checked ? 1 : 0 }))}
                              className="mb-2"
                            />
                          </div>
                        </div>
                      </div>

                      {/* Seção: Locais de Impressão */}
                      <div className="mb-4">
                        <h6 className="text-primary mb-3 border-bottom pb-2">
                          <i className="fas fa-print me-2"></i>Locais de Impressão
                        </h6>
                        <div className="row">
                          {(() => {
                            const printersArray = bulkAttributes.xd_printers || '0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0';
                            return printersArray.split(',').map((item: string, index: number) => {
                              const colClass = index < 10 ? 'col-md-6' : 'col-md-6';
                              return (
                                <div key={index} className={colClass}>
                                  <Form.Check
                                    type="checkbox"
                                    id={`printer-${index}`}
                                    label={`Local de impressão ${index + 1}`}
                                    checked={item === '1'}
                                    onChange={(e) => {
                                      const newArray = [...printersArray.split(',')];
                                      newArray[index] = e.target.checked ? '1' : '0';
                                      setBulkAttributes(prev => ({ ...prev, xd_printers: newArray.join(',') }));
                                    }}
                                    className="mb-2"
                                  />
                                </div>
                              );
                            });
                          })()}
                        </div>
                      </div>

                      {/* Seção: Visibilidade */}
                      <div className="mb-4">
                        <h6 className="text-primary mb-3 border-bottom pb-2">
                          <i className="fas fa-eye me-2"></i>Configurações de Visibilidade
                        </h6>
                        <div className="row">
                          <div className="col-md-6">
                            <Form.Check
                              type="checkbox"
                              id="visible-delivery"
                              label="Visível Delivery"
                              checked={bulkAttributes.visible_delivery || false}
                              onChange={(e) => setBulkAttributes(prev => ({ ...prev, visible_delivery: e.target.checked }))}
                              className="mb-2"
                            />
                            <Form.Check
                              type="checkbox"
                              id="visible-digital-menu"
                              label="Visível Menu Digital"
                              checked={bulkAttributes.visible_digital_menu || false}
                              onChange={(e) => setBulkAttributes(prev => ({ ...prev, visible_digital_menu: e.target.checked }))}
                              className="mb-2"
                            />
                            <Form.Check
                              type="checkbox"
                              id="visible-online"
                              label="Visível Online"
                              checked={bulkAttributes.visible_online || false}
                              onChange={(e) => setBulkAttributes(prev => ({ ...prev, visible_online: e.target.checked }))}
                              className="mb-2"
                            />
                            <Form.Check
                              type="checkbox"
                              id="visible-xd-orders"
                              label="Visível XD Pedidos"
                              checked={bulkAttributes.visible_xd_orders || false}
                              onChange={(e) => setBulkAttributes(prev => ({ ...prev, visible_xd_orders: e.target.checked }))}
                              className="mb-2"
                            />
                          </div>
                          <div className="col-md-6">
                            <Form.Check
                              type="checkbox"
                              id="visible-falimix"
                              label="Visível Falimix"
                              checked={bulkAttributes.visible_falimix || false}
                              onChange={(e) => setBulkAttributes(prev => ({ ...prev, visible_falimix: e.target.checked }))}
                              className="mb-2"
                            />
                            <Form.Check
                              type="checkbox"
                              id="visible-autoplu"
                              label="Visível AutoPLU"
                              checked={bulkAttributes.visible_autoplu || false}
                              onChange={(e) => setBulkAttributes(prev => ({ ...prev, visible_autoplu: e.target.checked }))}
                              className="mb-2"
                            />
                          </div>
                        </div>
                      </div>

                      {/* Seção: Outras Configurações */}
                      <div className="mb-4">
                        <h6 className="text-primary mb-3 border-bottom pb-2">
                          <i className="fas fa-cogs me-2"></i>Outras Configurações
                        </h6>
                        <div className="row">
                          <div className="col-md-6">
                            <Form.Check
                              type="checkbox"
                              id="xd-exclude-minimum-consumption"
                              label="XD Excluir Consumo Mínimo"
                              checked={bulkAttributes.xd_exclude_minimum_consumption || false}
                              onChange={(e) => setBulkAttributes(prev => ({ ...prev, xd_exclude_minimum_consumption: e.target.checked }))}
                              className="mb-2"
                            />
                          </div>
                        </div>
                      </div>
                    </Col>
                  </Row>

                  {(applyToCategories || applyToProducts) && (
                    <div className="mt-3 p-3 bg-light rounded">
                      <strong>Resumo:</strong>
                      <p className="mb-1">
                        Aplicar para: <strong>
                          {[applyToCategories && 'Categorias', applyToProducts && 'Produtos'].filter(Boolean).join(' e ')}
                        </strong>
                      </p>
                      <p className="mb-0">
                        Loja: <strong>{storeMe?.storeActive?.name || 'N/A'}</strong>
                      </p>
                    </div>
                  )}
                </>
              )}
            </S.Container>
          )}
        </Modal.Body>
      
        <Modal.Footer>
          <Button variant="secondary" onClick={handleClose} disabled={isLoading || loading}>
            Cancelar
          </Button>
          <Button 
            variant="primary" 
            onClick={handleSalvar}
            disabled={
              isLoading || loading || (
                showBulkSection 
                  ? (!applyToCategories && !applyToProducts)
                  : (perfilSelecionado === 0 || categoriasSelecionadas.length === 0)
              )
            }
          >
            {isLoading || loading ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                Processando...
              </>
            ) : (
              showBulkSection ? 'Aplicar Atributos' : 'Vincular Categorias'
            )}
          </Button>
        </Modal.Footer>
      </S.ModalWrapper>

      {/* Modal de Confirmação */}
      <Modal show={showConfirmModal} onHide={() => setShowConfirmModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>
            ⚠️ Confirmação de Atualização em Massa
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="alert alert-warning">
            <h6>🔄 Esta ação irá atualizar em massa:</h6>
            <ul className="mb-3">
              {applyToCategories && <li>✅ Todas as categorias da loja</li>}
              {applyToProducts && <li>✅ Todos os produtos da loja</li>}
            </ul>
            
            <h6>⚠️ Consequências importantes:</h6>
            <ul className="mb-3">
              <li>🔄 As configurações atuais serão sobrescritas</li>
              <li>⏰ Esta operação pode levar alguns minutos</li>
              <li>🚫 Não será possível desfazer esta ação</li>
              <li>📊 Todos os itens selecionados terão as mesmas configurações</li>
            </ul>
            
            <p className="mb-0">
              <strong>Tem certeza que deseja continuar?</strong>
            </p>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowConfirmModal(false)}>
            ❌ Cancelar
          </Button>
          <Button variant="danger" onClick={handleConfirmBulkUpdate}>
            ✅ Confirmar Atualização
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
}